import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { PiArrowUpBold, PiArrowDownBold } from 'react-icons/pi'
import { ChevronDown } from 'lucide-react'
import { SortIcon } from '../Icons/SortIcon'
import { MobileColors } from '../../styles/mobileTokens'
import { SortPanel, SortOverlay, PanelContent } from './Mobile.styles'

// Header with icons
const SortHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${MobileColors.borderDefault};
  background: white;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HeaderLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 15px;
  font-weight: 400;
  color: ${MobileColors.textPrimary};
`

const HeaderDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${MobileColors.borderDefault};
  margin: 0 4px;
`

const SortIconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${MobileColors.textPrimary};
  font-size: 20px;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${MobileColors.textPrimary};
  cursor: pointer;
  padding: 4px;
  font-size: 24px;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.5;
  }
`

// Styled components matching screenshot design
const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const OptionItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${(props) =>
    props.$isSelected ? MobileColors.backgroundSelected : 'white'};
  cursor: pointer;
  font-size: 15px;
  color: ${MobileColors.textPrimary};
  transition: background 0.15s ease;
  user-select: none;

  &:hover {
    background: ${(props) =>
      props.$isSelected
        ? MobileColors.backgroundSelected
        : MobileColors.backgroundOverlay};
  }

  &:active {
    background: ${MobileColors.backgroundActive};
  }
`

const OptionLabel = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  flex: 1;
  font-weight: ${(props) => (props.$isSelected ? '500' : '400')};
`

const DirectionArrow = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${MobileColors.textPrimary};
  margin-left: 4px;
  font-size: 14px;
  line-height: 1;
  vertical-align: middle;
`

export interface SortOption {
  value: string
  label: string
  arrow?: 'up' | 'down' | null
}

export interface MobileSortPanelProps {
  isOpen: boolean
  currentSort: string
  options: SortOption[]
  onClose: () => void
  onSelect: (sort: string) => void
}

export const MobileSortPanel: React.FC<MobileSortPanelProps> = ({
  isOpen,
  currentSort,
  options,
  onClose,
  onSelect,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Lock body scroll and manage focus when panel is open
  useEffect(() => {
    if (isOpen) {
      // Store currently focused element
      previousFocusRef.current = document.activeElement as HTMLElement

      // Lock body scroll
      document.body.style.overflow = 'hidden'

      // Focus panel for screen readers
      panelRef.current?.focus()
    } else {
      // Restore body scroll
      document.body.style.overflow = ''

      // Restore focus to previously focused element
      previousFocusRef.current?.focus()
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Auto-apply on selection and close
  const handleSelect = (sortValue: string) => {
    onSelect(sortValue)
    onClose()
  }

  // Get current selection
  const currentOption =
    options.find((opt) => opt.value === currentSort) || options[0]

  return (
    <>
      <SortOverlay $isOpen={isOpen} onClick={onClose} />
      <SortPanel
        $isOpen={isOpen}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Sort search results"
        tabIndex={-1}
      >
        <SortHeader>
          <HeaderLeft>
            <SortIconWrapper>
              <SortIcon size={20} />
            </SortIconWrapper>
          </HeaderLeft>
          <HeaderLeft>
            <HeaderLabel>
              {currentOption.label}
              {currentOption.arrow && (
                <DirectionArrow>
                  {currentOption.arrow === 'up' ? (
                    <PiArrowUpBold />
                  ) : (
                    <PiArrowDownBold />
                  )}
                </DirectionArrow>
              )}
            </HeaderLabel>
            <HeaderDivider />
            <CloseButton onClick={onClose} aria-label="Close sort menu">
              <ChevronDown size={20} />
            </CloseButton>
          </HeaderLeft>
        </SortHeader>

        <PanelContent>
          <OptionsList>
            {options.map((option) => (
              <OptionItem
                key={option.value}
                $isSelected={currentSort === option.value}
                onClick={() => handleSelect(option.value)}
              >
                <OptionLabel $isSelected={currentSort === option.value}>
                  {option.label}
                  {option.arrow && (
                    <DirectionArrow>
                      {option.arrow === 'up' ? (
                        <PiArrowUpBold />
                      ) : (
                        <PiArrowDownBold />
                      )}
                    </DirectionArrow>
                  )}
                </OptionLabel>
              </OptionItem>
            ))}
          </OptionsList>
        </PanelContent>
      </SortPanel>
    </>
  )
}
